import { useState, FormEvent, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { rememberEngineerSchools, useAuthStore } from '@/store'
import { getPublicJumppage, type JumppageResponse } from '@/services/api/jumppage'
import { getPublicSystemBranding, isSafeHttpUrl, SYSTEM_BRANDING_PUBLIC_KEY } from '@/services/api/systemBranding'
import { SchoolLoginFrame } from '@/components/jumppage/SchoolLoginFrame'
import { isStandaloneDisplay } from '@/utils/pwaDisplay'
import { isPhoneDevice } from '@/utils/talkWelt'
import { clearPendingLoginCode, readPendingLoginCode, storePendingLoginCode } from '@/utils/pendingLoginCode'
import { resolvePostLoginPath } from '@/utils/authRedirect'

const RESEND_COOLDOWN_SECONDS = 30

// Feste, sinnvolle Defaults für alles, was nicht mehr konfigurierbar ist.
const DEFAULT_BUTTON_LABEL = 'Login-Link senden'
const DEFAULT_SUCCESS_TITLE = 'E-Mail gesendet'
const DEFAULT_SUCCESS_MESSAGE = 'Prüfen Sie Ihr Postfach und klicken Sie auf den erhaltenen Link.'

export function JumppageLogin() {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, verifyCode, isLoginSubmitting: loginLoading, error: loginError, clearError } = useAuthStore()
  const [code, setCode] = useState('')
  const standalone = isStandaloneDisplay()
  const showCode = standalone || isPhoneDevice()

  const [jumppage, setJumppage] = useState<JumppageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [email, setEmail] = useState(() => readPendingLoginCode() ?? '')
  const [submitted, setSubmitted] = useState(() => readPendingLoginCode() != null)
  const [cooldown, setCooldown] = useState(0)

  // Button hover state
  const [btnHover, setBtnHover] = useState(false)
  // Input focus state
  const [inputFocus, setInputFocus] = useState(false)

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return }
    setLoading(true)
    setNotFound(false)
    getPublicJumppage(slug)
      .then((data) => { setJumppage(data); setNotFound(false) })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [slug])

  const { data: systemBranding } = useQuery({
    queryKey: SYSTEM_BRANDING_PUBLIC_KEY,
    queryFn: getPublicSystemBranding,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearError()
    try {
      await login(email)
      storePendingLoginCode(email)
      setSubmitted(true)
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch {
      // handled by auth store
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Laden...</div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <h2 className="text-2xl font-bold text-gray-900">JUMPPAGE nicht gefunden</h2>
          <p className="text-gray-600">Diese personalisierte Login-Seite existiert nicht oder wurde deaktiviert.</p>
          <Link to="/login" className="text-blue-600 hover:text-blue-500 font-medium">
            Zum allgemeinen Login zuruck
          </Link>
        </div>
      </div>
    )
  }

  if (!jumppage) return null

  const texts = jumppage.texts
  const design = jumppage.design

  const titleColor = design.text_color
  const subtitleColor = design.muted_color
  const btnBg = btnHover ? design.button_hover_color : design.button_bg_color
  const btnTextColor = design.button_text_color
  const inputBorder = inputFocus ? design.primary_color : design.border_color
  const surfaceColor = design.theme === 'dark' ? design.bg_color : '#ffffff'
  const systemLogo =
    systemBranding?.email_logo_png ||
    systemBranding?.email_logo_svg ||
    systemBranding?.logo_png ||
    systemBranding?.logo_svg ||
    null
  const systemLogoUrl = isSafeHttpUrl(systemBranding?.url) ? systemBranding?.url : null

  return (
    <SchoolLoginFrame
      design={design}
      schoolName={jumppage.school_name}
      title={submitted ? DEFAULT_SUCCESS_TITLE : texts.title}
      subtitle={texts.subtitle}
      systemLogo={systemLogo}
      systemLogoUrl={systemLogoUrl}
    >
      {submitted ? (
        <div className="mt-8 text-center">
          <div className="mb-4 text-lg" style={{ color: design.button_bg_color }}>{DEFAULT_SUCCESS_TITLE}</div>
          <p className="mb-4" style={{ color: subtitleColor }}>
            {showCode ? t('login_success_text_pwa') : DEFAULT_SUCCESS_MESSAGE}
          </p>
          {showCode && (
            <form className="mb-6 space-y-4 text-left" onSubmit={async (e) => {
              e.preventDefault()
              clearError()
              try {
                const response = await verifyCode(email, code)
                clearPendingLoginCode()
                if (rememberEngineerSchools(response)) {
                  navigate('/engineer/schools', { replace: true })
                  return
                }
                navigate(resolvePostLoginPath(useAuthStore.getState().user?.role), { replace: true })
              } catch { /* store */ }
            }}>
              <input
                id="login-code"
                name="one-time-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border text-center tracking-[0.4em] text-lg"
                style={{ backgroundColor: surfaceColor, borderColor: inputBorder, color: titleColor }}
                placeholder="000000"
              />
              {loginError && <div className="text-red-600 text-sm">{loginError}</div>}
              <button
                type="submit"
                disabled={loginLoading || code.length !== 6}
                className="w-full py-2 px-4 rounded-md text-sm font-medium"
                style={{ backgroundColor: btnBg, color: btnTextColor }}
              >
                {t('login')}
              </button>
            </form>
          )}
          {cooldown > 0
            ? <p className="text-sm" style={{ color: subtitleColor }}>{t('login_resend_in', { seconds: cooldown })}</p>
            : (
              <button
                type="button"
                onClick={() => {
                  clearPendingLoginCode()
                  setSubmitted(false)
                }}
                className="text-sm font-medium"
                style={{ color: design.button_bg_color }}
              >
                {t('login_back')}
              </button>
            )
          }
        </div>
      ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="sr-only">{t('email')}</label>
            <input
              id="email" name="email" type="email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setInputFocus(true)}
              onBlur={() => setInputFocus(false)}
              className="appearance-none rounded-md relative block w-full px-3 py-2 border focus:outline-none focus:z-10 sm:text-sm"
              style={{ backgroundColor: surfaceColor, borderColor: inputBorder, boxShadow: inputFocus ? `0 0 0 2px ${design.primary_color}40` : 'none', color: titleColor }}
              placeholder={t('email_placeholder')}
            />
          </div>
          {loginError && <div className="text-red-600 text-sm">{loginError}</div>}
          <div>
            <button
              type="submit" disabled={loginLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: btnBg, color: btnTextColor }}
              onMouseEnter={() => setBtnHover(true)}
              onMouseLeave={() => setBtnHover(false)}
            >
              {loginLoading ? t('login_sending') : DEFAULT_BUTTON_LABEL}
            </button>
          </div>
          {texts.hint_text && <p className="text-xs text-center" style={{ color: subtitleColor }}>{texts.hint_text}</p>}
        </form>
      )}
    </SchoolLoginFrame>
  )
}
